import type { MiddlewareConfig } from './middlewareComposer.ts';
import { createSSRFetch, createSSRTestFetch } from './ssrFetch.ts';
import { createNetworkStatusMiddleware } from './networkStatusMiddleware.ts';
import { createStatusCodeRetryMiddleware } from './StatusCodeRetryMiddleware.ts';

export interface MiddlewareEnvironment {
	isSSR: boolean;
	isSSRTest: boolean;
}

/**
 * Returns middleware configuration for Relay network layer.
 *
 * Listed innermost → outermost, because `composeMiddlewares` makes the last entry outermost. The
 * resulting client-side nesting is `NetworkStatus(StatusCodeRetry(fetch))`, which mirrors Apollo's
 * chain in `createClient.ts`:
 *
 *     networkStatusLink()      <- outer
 *     statusCodeRetryLink      <- inner
 *     networkErrorRetryLink
 *     httpLink
 *
 * The relative order of those two matters: `NetworkStatus` swallows some errors (403, and offline
 * when the bubble gate is off) without terminating the observable, so anything outside it never sees
 * them. Putting `StatusCodeRetry` inside means it still gets a chance to retry a 403 first, as it does
 * on Apollo. Inverting them silently disables status-code retries for every swallowed status.
 */
export const getMiddlewareConfig = (env: MiddlewareEnvironment): MiddlewareConfig[] => {
	const { isSSR, isSSRTest } = env;

	return [
		{
			name: 'SSRFetch',
			factory: createSSRFetch,
			enabled: isSSR,
		},
		{
			name: 'SSRTestFetch',
			factory: createSSRTestFetch,
			enabled: isSSR && isSSRTest,
		},
		{
			name: 'StatusCodeRetry',
			factory: createStatusCodeRetryMiddleware,
			enabled: !isSSR,
		},
		{
			name: 'NetworkStatus',
			factory: createNetworkStatusMiddleware,
			enabled: !isSSR,
		},
	];
};
